import clsx from "clsx";
import React, { Fragment, FunctionComponent } from "react";
import { useTranslation } from "react-i18next";
import { useToggle } from "react-use";

import { StockItem } from "../../../../queries/useSearchTiresByVehicle";
import { useNullableDealerAccountInfo } from "../../../../stores/hooks/useNullableDealerAccountInfo";
import { Button } from "../../../../ui/Button/Button";
import { Dropdown } from "../../../../ui/Dropdown";
import { TimeAgo } from "../../../../ui/TimeAgo/TimeAgo";
import { Toggle } from "../../../../ui/Toggle";
import { ChevronRightIcon } from "../../../../ui/icons/ChevronRightIcon";
import { TireLinkLogo } from "../../../TireLinkLogo/TireLinkLogo";
import { ModifiedTirePartItem } from "../useTireResults";
import { StockLevels } from "./StockLevels ";
import { StockRecord } from "./StockRecord";

type DealerTireStockProps = {
  allStock: StockItem[];
  dlrStock: StockItem[];
  dropdownId: string;
  isRestricted: boolean;
  part: ModifiedTirePartItem;
  rltStock: StockItem[];
};

export const countStock = (stock: StockItem[]): number =>
  stock.reduce((acc, { stockQty }) => acc + Number(stockQty), 0);

export const filterStock = (
  stock: StockItem[],
  locationType: string
): StockItem[] =>
  stock.filter(
    ({ stockLocationType, stockQty }) =>
      stockLocationType === locationType && Number(stockQty) > 0
  );

export const formatCount = (
  count: number | string,
  maxCount: number = 20
): string => {
  const value = Number(count);
  if (value > maxCount) {
    return "20+";
  }
  return count.toString();
};

export const TireStock: FunctionComponent<DealerTireStockProps> = ({
  allStock,
  dlrStock,
  dropdownId,
  isRestricted,
  part,
  rltStock,
}) => {
  const { t } = useTranslation();
  const [open, toggle] = useToggle(false);
  const dealer = useNullableDealerAccountInfo();

  const locStock = filterStock(allStock, "LOC");

  const dlrStockCount = countStock(dlrStock);
  const locStockCount = countStock(locStock);
  const rtlStockCount = countStock(rltStock);
  const buttonId = `${dropdownId}-button`;

  if (isRestricted && !locStockCount) {
    return null;
  }

  return (
    <div className="bg-paper-2 text-ink bg-opacity-90 text-sm">
      <Toggle disabled={!allStock.length || isRestricted} onToggle={toggle}>
        <Button
          className={clsx(
            "px-4 md:px-8 h-10 rounded-none flex w-full justify-between",
            isRestricted && "pointer-events-none"
          )}
          aria-label="tireStockToggle"
          disabled={!allStock.length}
          id={buttonId}
        >
          <span className="text-left flex flex-1 whitespace-nowrap truncate gap-1 items-center">
            {[
              !isRestricted && rltStock.length > 0 && (
                <>
                  <TireLinkLogo className="h-4 -translate-y-[0.15em] -ml-1 -mr-1" />
                  : {formatCount(rtlStockCount)}
                </>
              ),
              locStockCount > 0 && (
                <>
                  {t("dealerTireTile.tires", {
                    count: locStockCount,
                    displayCount: formatCount(locStockCount),
                  })}
                  <span>
                    {" "}
                    {t("dealerTireTile.inLocation", {
                      count: locStock.length,
                    })}
                  </span>
                </>
              ),
              !isRestricted && dlrStockCount > 0 && (
                <>
                  {t("dealerTireTile.tires", {
                    count: dlrStockCount,
                    displayCount: formatCount(dlrStockCount),
                  })}
                </>
              ),
            ]
              .filter(Boolean)
              .map((item, key) => (
                <Fragment key={key}>
                  {!!key && <span className="opacity-40">|</span>}
                  {item}
                </Fragment>
              ))}
          </span>
          {!isRestricted && (
            <ChevronRightIcon
              className={clsx(
                "transform transition-transform duration-300",
                open ? "rotate-90" : "-rotate-90"
              )}
            />
          )}
        </Button>
        <Dropdown
          aria-labelledby={buttonId}
          blockLevel
          className="bg-paper-2 rounded-t-3xl rounded-b-none z-10 py-2 max-h-56 overflow-y-auto"
          id={dropdownId}
          placement="top"
          role="menu"
        >
          {dlrStock.map(({ stockLocationId, stockLocationName, stockQty }) => (
            <StockRecord key={stockLocationId}>
              {stockLocationName}
              {t("dealerTireTile.tires", {
                count: Number(stockQty),
                displayCount: formatCount(stockQty),
              })}
              {t("dealerTireTile.availableStock")}
            </StockRecord>
          ))}
          {rltStock.map(
            ({ stockLocationId, stockQtyLocal, stockQtyLocalPlus }) => (
              <StockRecord key={stockLocationId}>
                <>TireLink</>
                <>{formatCount(stockQtyLocal)} Local DC</>
                <>{formatCount(stockQtyLocalPlus)} Local+</>
              </StockRecord>
            )
          )}
          {(dlrStock.length || rltStock.length) > 0 && locStock.length > 0 && (
            <hr className="mx-4 md:mx-8 my-1 border-ink opacity-40" />
          )}
          {locStock.map(
            ({
              minutesSinceLastStockUpdate,
              stockLocationId,
              stockLocationName,
              stockLocationWebsite,
              stockQty,
            }) => (
              <StockRecord key={stockLocationId} link={stockLocationWebsite}>
                {stockLocationName}
                {t("dealerTireTile.tires", {
                  count: Number(stockQty),
                  displayCount: formatCount(stockQty),
                })}
                <TimeAgo minutes={minutesSinceLastStockUpdate} />
              </StockRecord>
            )
          )}
          {dealer?.dealerEnableNationalStockCheck && (
            <>
              <hr className="mx-4 md:mx-8 my-1 border-ink opacity-40" />
              <StockLevels part={part} />
            </>
          )}
        </Dropdown>
      </Toggle>
    </div>
  );
};
